import React from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import omit from 'lodash/omit';

import styles from './FileUploadForm.css';
import FormSubmissionControls from './FormSubmissionControls.js';

const EMPTY_DATA = {
  title: '',
  subtitle: '',
  content: '',
  filename: '',
  s3_filename: ''
};
const FILE_UPLOAD_MESSAGES = {
  FILE_TYPE_PDF: 'ERROR: File type must be PDF (.pdf)',
  FILE_TYPE_SWF: 'ERROR: File type must be Shockwave Flash (.swf)',
  SIZE_LIMIT: 'ERROR: File must be under 8MB',
  UNKNOWN_ERROR: 'ERROR: Please try again later.',
  UPLOADING: 'Uploading file...'
};
const FILE_SIZE_LIMIT = 8388608;

export default class FileUploadForm extends React.Component {
  constructor(props) {
    super(props);

    this.getDescription = this.getDescription.bind(this);
    this.getDefaultMessage = this.getDefaultMessage.bind(this);
    this.handleUploadResponse = this.handleUploadResponse.bind(this);
    this.isValid = this.isValid.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onTextInputChange = this.onTextInputChange.bind(this);

    this.state = Object.assign({
      fileUploadMessage: this.getDefaultMessage(props.data.filename),
      formValidationText: ''
    }, props.data);
  }

  onTextInputChange(stateKey) {
    return (e) => {
      const text = e.target.value;
      this.setState({[stateKey]: text});
    };
  }

  onCancel() {
    this.setState(EMPTY_DATA);
    this.props.onCancel();
  }

  onDrop(files) {
    const { fileExtension, fileUploadUrl } = this.props;
    // There will only be 1 file thanks to Dropzone `multiple=false` prop
    const file = files[0];

    if(file.size > FILE_SIZE_LIMIT) {
      this.setState({ fileUploadMessage: FILE_UPLOAD_MESSAGES.SIZE_LIMIT });
    } else if(fileExtension === 'pdf' && file.type !== 'application/pdf') {
      this.setState({ fileUploadMessage: FILE_UPLOAD_MESSAGES.FILE_TYPE_PDF });
    } else if(fileExtension === 'swf' && file.type !== 'application/x-shockwave-flash') {
      this.setState({ fileUploadMessage: FILE_UPLOAD_MESSAGES.FILE_TYPE_SWF });
    } else {
      // validation passed, upload file
      this.setState({ fileUploadMessage: FILE_UPLOAD_MESSAGES.UPLOADING });

      /* eslint-disable no-undef */
      const data = new FormData();
      data.append('file', file);

      const request = new XMLHttpRequest();
      /* eslint-enable no-undef */
      request.open('POST', fileUploadUrl, true);
      request.onload = this.handleUploadResponse;
      request.onerror = () => {
        this.setState({ fileUploadMessage: FILE_UPLOAD_MESSAGES.UNKNOWN_ERROR });
      };
      request.send(data);
    }
  }

  onSubmit() {
    if(! this.isValid()) {
      return; // early exit
    }

    const { fileExtension, onSubmit } = this.props;
    const newData = Object.assign(
      {
        categories: [
          'custom',
          fileExtension
        ],
        subtype: fileExtension,
        icon: fileExtension.toUpperCase()
      },
      omit(this.state, 'fileUploadMessage')
    );

    onSubmit(newData);
  }

  getDescription() {
    // We're doing this to allow marked-up descriptions.
    // E.g., The description for SWF upload includes some italics.
    // @see: https://facebook.github.io/react/tips/dangerously-set-inner-html.html
    return {
      '__html': this.props.description
    };
  }

  getDefaultMessage(filename) {
    let message = '';

    if(filename !== undefined && filename !== '') {
      message += `Current file is: ${filename}. `;
    }

    message += `Drag and drop file here, or click to select one to upload.
      File size must be less than 8MB`;
    return message;
  }

  handleUploadResponse(event) {
    const request = event.target;

    if(request.status >= 200 && request.status < 400) {
      const {
        filelink,
        filename,
        s3_filename
      } = JSON.parse(request.responseText)['file-1'];

      this.setState({
        content: filelink,
        filename,
        fileUploadMessage: this.getDefaultMessage(filename),
        s3_filename
      });
    } else {
      this.setState({ fileUploadMessage: FILE_UPLOAD_MESSAGES.UNKNOWN_ERROR });
    }
  }

  isValid() {
    const { title, content } = this.state;
    const validityMessages = [];

    if(title.length === 0) {
      validityMessages.push('Title is required');
    }
    if(content.length === 0) {
      validityMessages.push('File Upload is required');
    }

    const isValid = validityMessages.length === 0;
    if(! isValid) {
      this.setState({
        formValidationText: validityMessages.join('; ')
      });
    }

    return isValid;
  }

  render() {
    const { fileExtension, isEditing } = this.props;
    const { fileUploadMessage, formValidationText } = this.state;

    const formValidationMessage = (
      <p className={styles.formValidationMessage}>
        {formValidationText}
      </p>
    );

    return (
      <form>
        <p dangerouslySetInnerHTML={this.getDescription()} />

        <section>
          <div className={styles.formField}>
            <label className={styles.label}>Title</label>
            <input
              type="text"
              value={this.state.title}
              onChange={this.onTextInputChange('title')}
              className={styles.textInput}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.label}>Subtitle</label>
            <input
              type="text"
              value={this.state.subtitle}
              placeholder="Optional"
              onChange={this.onTextInputChange('subtitle')}
              className={styles.textInput}
            />
          </div>

          <div className={styles.fileFormField}>
            <label className={styles.label}>
              {`${fileExtension.toUpperCase()} File`}
            </label>
            <Dropzone
              onDrop={this.onDrop}
              multiple={false}
            >
              {({getRootProps, getInputProps}) => (
                <section>
                  <div {...getRootProps({className: styles.fileUploader})}>
                    <input {...getInputProps()} />
                    <p className={styles.fileUploadMessage}>{fileUploadMessage}</p>
                  </div>
                </section>
              )}
            </Dropzone>

          </div>
        </section>

        {formValidationText.length > 0 ? formValidationMessage: null}

        <FormSubmissionControls
          onCancel={this.onCancel}
          onSubmit={this.onSubmit}
          submitButtonText={isEditing ? 'Save' : undefined}
        />
      </form>
    );
  }
}

FileUploadForm.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    content: PropTypes.string,
    filename: PropTypes.string,
    s3_filename: PropTypes.string
  }),
  description: PropTypes.string,
  fileExtension: PropTypes.string.isRequired,
  fileUploadUrl: PropTypes.string.isRequired,
  isEditing: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

FileUploadForm.defaultProps = {
  data: EMPTY_DATA,
  description: '',
  isEditing: false
};
